import React, {useEffect} from "react";
import PropTypes from "prop-types";
import {Query, useQuery} from "react-apollo";
import get from "lodash/get";
import pickBy from "lodash/pickBy";
import {
    FullWidthContainer,
    WideContainer,
} from "@unibuddy/machop/Experimental/Layout/components/Container/Container";
import {rootURL} from "@unibuddy/machop/Utils/WhereAmI";
import PoweredByUnibuddy from "@unibuddy/machop/General/components/PoweredByUnibuddy/PoweredByUnibuddy";
import {useBuddiesFilters} from "@unibuddy/machop/Buddies/components/BuddiesFiltersProvider/BuddiesFiltersProvider";
import ForwardLoader from "@unibuddy/machop/General/components/ForwardLoader/ForwardLoader";
import useAnalytics from "@unibuddy/machop/AnalyticsNew/hooks/useAnalytics";
import buddiesFilterOptionsQuery from "@unibuddy/machop/Buddies/queries/buddiesFilterOptionsQuery";
import ErrorDisplay from "@unibuddy/machop/General/components/ErrorDisplay/ErrorDisplay";
import {useTranslation} from "react-i18next";
import PageHeader from "@unibuddy/machop/Shared/components/PageHeader/PageHeader";
import PageTitle from "@unibuddy/machop/Shared/components/PageTitle/PageTitle";
import buddiesQuery from "../../queries/buddiesQuery";
import BuddyListCard from "../BuddyListCard/BuddyListCard";
import s from "./BuddiesList.pcss";
import BuddiesFilterSection from "../BuddiesFilterSection/BuddiesFilterSection";

const staffFilterFields = ["role", "department"];

const updateQuery = (prev, {fetchMoreResult}) => {
    if (!fetchMoreResult) return prev;

    return {
        ...prev,
        buddies: {
            ...prev.buddies,
            users: [...prev.buddies.users, ...fetchMoreResult.buddies.users],
        },
    };
};

const processFilters = (filters, buddyGroupType) => {
    if (buddyGroupType === "staff") {
        return {staffFields: JSON.stringify(pickBy(filters))};
    }
    return filters;
};

const onChatNow = ({compProps, buddyId, rootUrl, buddyPosition, buddyGroupType}) => {
    compProps.history.push(
        `${rootUrl}/inbox/chatwith/${buddyId}?buddyPosition=${buddyPosition}&buddyGroupType=${buddyGroupType}`,
    );
};

const onSeeMore = ({compProps, buddyId, rootUrl, buddyPosition, buddyGroupType}) => {
    compProps.history.push(
        `${rootUrl}/buddies/${buddyGroupType}/${buddyId}?buddyPosition=${buddyPosition}&buddyGroupType=${buddyGroupType}`,
    );
};

const onAvatarClicked = ({compProps, buddyId, rootUrl, buddyPosition, buddyGroupType}) => {
    compProps.history.push(
        `${rootUrl}/buddies/${buddyGroupType}/${buddyId}?buddyPosition=${buddyPosition}&buddyGroupType=${buddyGroupType}`,
    );
};

export default function BuddiesList({limit, match, history}) {
    const {t} = useTranslation("common");

    const buddyGroupType = get(match, "params.buddyGroupType", "staff");
    const universitySlug = get(match, "params.universitySlug");
    const {data: buddiesFilterOptionsData} = useQuery(buddiesFilterOptionsQuery, {
        variables: {
            universitySlug,
            groupType: buddyGroupType,
            active: true,
        },
    });
    const {filters, setFilters} = useBuddiesFilters();
    const staffFiltersObject = staffFilterFields.reduce(
        (prev, curr) => (filters[curr] ? {...prev, [curr]: filters[curr]} : prev),
        {},
    );
    const customAttributesFilterOptions =
        get(buddiesFilterOptionsData, "buddiesFilterOptions.customAttributesFilters", []) || [];
    const customAttributesFiltersObject = customAttributesFilterOptions.reduce(
        (prev, curr) => (filters[curr.id] ? {...prev, [curr.id]: filters[curr.id]} : prev),
        {},
    );
    const customAttributesFilters = Object.keys(customAttributesFiltersObject).map(key => ({
        field: key,
        value: filters[key],
    }));

    const variables = {
        universitySlug,
        role: "staff",
        offset: 0,
        limit,
        customAttributesFilters: customAttributesFilters.length
            ? customAttributesFilters
            : undefined,
        ...processFilters(staffFiltersObject, buddyGroupType),
    };
    const {
        events: {buddiesListViewed},
    } = useAnalytics();
    useEffect(
        () => {
            buddiesListViewed({
                buddyType: buddyGroupType,
            });
        },
        [buddiesListViewed, buddyGroupType],
    );
    return (
        <Query
            fetchPolicy="cache-and-network"
            query={buddiesQuery}
            notifyOnNetworkStatusChange
            variables={variables}
        >
            {({data, error, fetchMore, networkStatus}) => {
                let errorJsx = null;
                const buddyCount = get(data, "buddies.count", 0);
                const buddies = get(data, "buddies.users", []);
                const randomizeListSeed = get(data, "buddies.randomizeListSeed", null);
                if (networkStatus === 1) {
                    errorJsx = (
                        <div className={s.informationText} data-test-id="buddies-list-loading">
                            Loading...
                        </div>
                    );
                } else if (!buddyCount) {
                    errorJsx = (
                        <div>
                            <div className={s.informationText}>
                                {t(
                                    "noAmbassadorMsg",
                                    "Sorry, no ambassador is matching your filters.",
                                )}
                            </div>
                        </div>
                    );
                }
                const hasMore = buddies.length < buddyCount;
                const compProps = {limit, match, history};
                return (
                    <ErrorDisplay error={networkStatus !== 1 && error ? error : null}>
                        <div id="buddies-page" dataTestId={"buddies-page"}>
                            <PageTitle title={t(buddyGroupType, "Staff")} />
                            <WideContainer>
                                <PageHeader
                                    autofocus
                                    className={s.title}
                                    data-test-id="title"
                                    title={`${t(buddyGroupType, "Staff")} `}
                                    rightContent={<PoweredByUnibuddy redirectOnClick />}
                                />
                                <React.Suspense fallback={"loading..."}>
                                    <BuddiesFilterSection
                                        buddyGroupType={buddyGroupType}
                                        filters={{
                                            ...staffFiltersObject,
                                            ...customAttributesFiltersObject,
                                        }}
                                        setFilters={updatedfilters => {
                                            return setFilters({...filters, ...updatedfilters});
                                        }}
                                        buddiesFilterOptionsData={buddiesFilterOptionsData}
                                    />
                                </React.Suspense>
                            </WideContainer>
                            <FullWidthContainer>
                                {!errorJsx ? (
                                    <React.Fragment>
                                        <div data-test-id="buddies-list" className={s.list}>
                                            {buddies.map((buddy, buddyPosition) => {
                                                return (
                                                    <div
                                                        data-test-id="buddy-card-container"
                                                        key={buddy.id}
                                                        className={s.cardContainer}
                                                    >
                                                        <BuddyListCard
                                                            buddy={buddy}
                                                            rootUrl={`${rootURL(
                                                                match.url,
                                                            )}/buddies/${buddyGroupType}/${
                                                                buddy.id
                                                            }`}
                                                            onChatNow={() =>
                                                                onChatNow({
                                                                    compProps,
                                                                    buddyGroupType,
                                                                    buddyPosition,
                                                                    buddyId: buddy.id,
                                                                    rootUrl: rootURL(match.url),
                                                                })
                                                            }
                                                            onSeeMore={() =>
                                                                onSeeMore({
                                                                    compProps,
                                                                    buddyGroupType,
                                                                    buddyPosition,
                                                                    buddyId: buddy.id,
                                                                    rootUrl: rootURL(match.url),
                                                                })
                                                            }
                                                            onAvatarClicked={() =>
                                                                onAvatarClicked({
                                                                    compProps,
                                                                    buddyGroupType,
                                                                    buddyPosition,
                                                                    buddyId: buddy.id,
                                                                    rootUrl: rootURL(match.url),
                                                                })
                                                            }
                                                            isInternal
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {hasMore ? (
                                            <ForwardLoader
                                                fetchMore={() =>
                                                    fetchMore({
                                                        variables: {
                                                            offset: buddies.length,
                                                            randomizeListSeed,
                                                        },
                                                        updateQuery,
                                                    })
                                                }
                                                showLoadButton={hasMore}
                                            />
                                        ) : null}
                                    </React.Fragment>
                                ) : (
                                    errorJsx
                                )}
                            </FullWidthContainer>
                        </div>
                    </ErrorDisplay>
                );
            }}
        </Query>
    );
}

BuddiesList.propTypes = {
    limit: PropTypes.number,
};

BuddiesList.defaultProps = {
    limit: 12,
};
